import { useEffect, useState } from "react";
import { Check, Loader2, Plane } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePutVacation, useVacation } from "@/api/hooks";
import { AiTextGenerator } from "./AiTextGenerator";

export function VacationPage() {
    const vacation = useVacation();
    const put = usePutVacation();

    const [form, setForm] = useState({
        isEnabled: false,
        subject: "",
        bodyHtml: "",
        startAt: "",
        endAt: ""
    });

    useEffect(() => {
        if (!vacation.data) return;
        setForm({
            isEnabled: vacation.data.isEnabled,
            subject: vacation.data.subject,
            bodyHtml: vacation.data.bodyHtml,
            startAt: vacation.data.startAt ? vacation.data.startAt.slice(0, 16) : "",
            endAt: vacation.data.endAt ? vacation.data.endAt.slice(0, 16) : ""
        });
    }, [vacation.data]);

    const submit = () => {
        put.mutate({
            isEnabled: form.isEnabled,
            subject: form.subject,
            bodyHtml: form.bodyHtml,
            startAt: form.startAt ? new Date(form.startAt).toISOString() : null,
            endAt: form.endAt ? new Date(form.endAt).toISOString() : null
        });
    };

    return (
        <div className="mx-auto max-w-3xl">
            <header className="mb-4 flex items-center gap-2">
                <Plane className="h-6 w-6 text-primary" />
                <div>
                    <h2 className="text-xl font-semibold">Out of Office</h2>
                    <p className="text-sm text-muted-foreground">
                        Automatically reply while you're away. Toggle on, set a window, write the message.
                    </p>
                </div>
            </header>

            <section className="rounded-lg border bg-card p-5 shadow-sm">
                <label className="mb-4 flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={form.isEnabled}
                        onChange={(e) => setForm((f) => ({ ...f, isEnabled: e.target.checked }))}
                    />
                    <span className="text-sm font-medium">Enable auto-reply</span>
                </label>

                <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Start (optional)">
                        <Input
                            type="datetime-local"
                            value={form.startAt}
                            onChange={(e) => setForm((f) => ({ ...f, startAt: e.target.value }))}
                        />
                    </Field>
                    <Field label="End (optional)">
                        <Input
                            type="datetime-local"
                            value={form.endAt}
                            onChange={(e) => setForm((f) => ({ ...f, endAt: e.target.value }))}
                        />
                    </Field>
                </div>

                <div className="mt-3">
                    <Field label="Subject">
                        <Input
                            value={form.subject}
                            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                            placeholder="Out of office"
                        />
                    </Field>
                </div>

                <div className="mt-3">
                    <div className="mb-1 flex items-center justify-between gap-2">
                        <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Reply message
                        </label>
                        <AiTextGenerator
                            mode="autoreply"
                            title="Draft an out-of-office reply"
                            firstPlaceholder='Describe it — e.g. "away on holiday, back Monday, urgent → contact ops@…"'
                            buildInitial={(input) => [
                                form.startAt && `Away from: ${form.startAt}`,
                                form.endAt && `Returning: ${form.endAt}`,
                                input.trim() || "Write a friendly out-of-office auto-reply."
                            ].filter(Boolean).join("\n")}
                            onApply={(html) => setForm((f) => ({ ...f, bodyHtml: html }))}
                        />
                    </div>
                    <textarea
                        value={form.bodyHtml}
                        onChange={(e) => setForm((f) => ({ ...f, bodyHtml: e.target.value }))}
                        rows={6}
                        placeholder="Thanks for your email. I'm away until..."
                        className="w-full rounded-md border p-2 text-sm"
                    />
                    <p className="mt-1 text-[10px] text-muted-foreground">
                        HTML is accepted; the BFF stores the raw string.
                    </p>
                </div>

                <div className="mt-4 flex items-center justify-end gap-2">
                    {put.isSuccess ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-600">
                            <Check className="h-3.5 w-3.5" /> Saved
                        </span>
                    ) : null}
                    <Button onClick={submit} disabled={put.isPending}>
                        {put.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        Save
                    </Button>
                </div>
            </section>
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </label>
            {children}
        </div>
    );
}
